import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { Api } from '../../api/api';
import { actions } from '../../state';
import { errorMessage } from '../../utils';
import { Button, Input, Select } from '../../components';

import styles from './bulkUpdateModal.module.scss';

const MembershipOrdersBulkUpdateModal = ({
  dispatch,
  memberships,
  membershipsLoading,
  onSuccess,
}) => {
  const [values, setValues] = useState({
    file: null,
    membership_id: '',
    valid_from: '',
    valid_until: '',
  });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );

    return () => {
      dispatch(actions.memberships.clearMembershipsAction());
    };
  }, [dispatch]);

  const membershipOptions = useMemo(
    () => [
      { name: 'Pasirinkite planą', value: '' },
      ...memberships.map(membership => ({
        name: membership.title_lt,
        value: membership.id,
      })),
    ],
    [memberships]
  );

  const handleInputChange = ({ target: { name, value } }) => {
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = ({ target: { files } }) => {
    setValues(prev => ({
      ...prev,
      file: files?.[0] || null,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (
      !values.file ||
      !values.membership_id ||
      !values.valid_from ||
      !values.valid_until
    ) {
      return;
    }

    if (values.valid_until < values.valid_from) {
      dispatch(
        actions.error.setErrorAction({
          msg: 'Data "Galioja iki" negali būti ankstesnė už "Galioja nuo"',
        })
      );
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const response = await Api.bulkAddMembershipOrders({
        file: values.file,
        membership_id: Number(values.membership_id),
        valid_from: values.valid_from,
        valid_until: values.valid_until,
      });

      setResult(response);
      onSuccess();
    } catch (e) {
      dispatch(actions.error.setErrorAction(errorMessage(e)));
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    membershipsLoading ||
    submitting ||
    !values.file ||
    !values.membership_id ||
    !values.valid_from ||
    !values.valid_until;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.field}>
        <p className={styles.label}>File įkėlimas</p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          disabled={submitting}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.label}>Planas</span>
        <Select
          name="membership_id"
          options={membershipOptions}
          selectedValue={values.membership_id}
          onChange={handleInputChange}
          disabled={membershipsLoading || submitting}
          className={styles.select}
        />
      </div>

      <div className={styles.dateFields}>
        <Input
          name="valid_from"
          type="date"
          label="Galioja nuo"
          max="2999-12-31"
          onChange={handleInputChange}
          value={values.valid_from}
          disabled={submitting}
          required
        />
        <Input
          name="valid_until"
          type="date"
          label="Galioja iki"
          max="2999-12-31"
          onChange={handleInputChange}
          value={values.valid_until}
          disabled={submitting}
          required
        />
      </div>

      <div className={styles.actions}>
        <Button color="primary" type="submit" disabled={isSubmitDisabled}>
          {submitting ? 'Vykdoma...' : 'Keisti planus'}
        </Button>
      </div>

      {result && (
        <div className={styles.noticeBox}>
          <span>Surasti klientai: {result.clients_matched ?? 0}</span>
          <span>Sustabdytos narystės: {result.paused_memberships ?? 0}</span>
          <span>Priskirtos narystės: {result.assigned_memberships ?? 0}</span>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = state => ({
  memberships: state.memberships.memberships,
  membershipsLoading: state.memberships.loading,
});

MembershipOrdersBulkUpdateModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  memberships: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title_lt: PropTypes.string.isRequired,
    })
  ).isRequired,
  membershipsLoading: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
};

MembershipOrdersBulkUpdateModal.defaultProps = {
  onSuccess: () => {},
};

export default connect(mapStateToProps)(MembershipOrdersBulkUpdateModal);
